import React, { useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import {
  useHouses,
  useHouse,
  useCreateHouse,
  useUpdateHouse,
  useDeleteHouse,
  useExportHouses,
  useExportHouseDetail,
  useImportHouses,
  useDownloadHouseTemplate,
} from '../hooks/useHouses.ts';
import { useDebounce } from '../hooks/useDebounce.ts';
import { useCreateMember, useUpdateMember } from '../hooks/useMembers.ts';
import { Plus, Edit, Trash, Eye, ArrowLeft, Home, Users, CreditCard, Calendar, FileText, UserPlus, UserX, UserCheck, Download, Upload, Info, Search } from 'lucide-react';
import Modal from '../components/Modal.tsx';
import Table from '../components/Table.tsx';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export default function HousesPage() {
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  const isAdmin = useIsAdmin();
  const currentGroup = profile?.data?.current_group;
  const isHouseManagement = Number(currentGroup?.is_house_management) === 1;
  const villaLabel = isHouseManagement ? (currentGroup?.house_label || 'House') : 'House';
  const tenantLabel = isHouseManagement ? (currentGroup?.tenant_label || 'Member') : 'Member';

  // Admins only — non-admins (e.g. role-switched, manually typed URL) land on their own villa.
  // Defer the redirect render so it doesn't sit between hook calls (rules-of-hooks).
  const shouldRedirect = !isProfileLoading && profile?.data && !isAdmin;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data: housesData, isLoading: isListLoading } = useHouses({ search: debouncedSearchQuery });
  const houses = housesData?.data || [];

  const [selectedId, setSelectedId] = useState(null);
  const { data: detailData, isLoading: isDetailLoading } = useHouse(selectedId);
  const detail = detailData?.data || null;

  // Pagination state
  const [page, setPage] = useState(1);
  const perPage = 10;

  const paginatedHouses = useMemo(() => {
    const start = (page - 1) * perPage;
    return houses.slice(start, start + perPage);
  }, [houses, page]);

  // Modals state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingHouse, setEditingHouse] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Mutations
  const createMutation = useCreateHouse();
  const updateMutation = useUpdateHouse();
  const deleteMutation = useDeleteHouse();
  const exportAllMutation = useExportHouses();
  const exportDetailMutation = useExportHouseDetail();
  const importMutation = useImportHouses();
  const templateMutation = useDownloadHouseTemplate();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportAll = async () => {
    const blob = await exportAllMutation.mutateAsync();
    downloadBlob(blob, `${villaLabel.toLowerCase()}_management_report.csv`);
  };

  const handleExportDetail = async (sub) => {
    const blob = await exportDetailMutation.mutateAsync(sub.id);
    downloadBlob(blob, `${villaLabel.toLowerCase()}_${(sub.name || 'detail').replace(/\s+/g, '_')}_ledger.csv`);
  };

  const handleDownloadTemplate = async () => {
    const blob = await templateMutation.mutateAsync();
    downloadBlob(blob, `${villaLabel.toLowerCase()}_import_template.csv`);
  };

  const handleImportSubmit = () => {
    if (!importFile) {
      toast.error('Please choose a CSV file');
      return;
    }
    importMutation.mutate(importFile, {
      onSuccess: () => {
        setImportFile(null);
        setIsImportOpen(false);
      },
    });
  };

  // Tenant (house member) management
  const queryClient = useQueryClient();
  const createMemberMutation = useCreateMember();
  const updateMemberMutation = useUpdateMember();
  const [isTenantModalOpen, setIsTenantModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [tenantForm, setTenantForm] = useState({ name: '', phone_number: '', email: '', member_no: '', status: 'active' });
  const [showAllMilestones, setShowAllMilestones] = useState(false);

  const openAddTenant = () => {
    setEditingTenant(null);
    setTenantForm({ name: '', phone_number: '', email: '', member_no: '', status: 'active' });
    setIsTenantModalOpen(true);
  };

  const openEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setTenantForm({
      name: tenant.name || '',
      phone_number: tenant.phone_number || '',
      email: tenant.email || '',
      member_no: tenant.member_no || '',
      status: tenant.is_active ? 'active' : 'inactive',
    });
    setIsTenantModalOpen(true);
  };

  const handleTenantSubmit = (e) => {
    e.preventDefault();
    if (!tenantForm.name.trim() || !tenantForm.phone_number.trim()) {
      return toast.error('Name and phone number are required');
    }
    const payload = {
      ...tenantForm,
      house_id: selectedId,
      contribute_to_multiple_drives: false,
    };
    const after = () => {
      queryClient.invalidateQueries({ queryKey: ['house', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['houses'] });
      setIsTenantModalOpen(false);
      setEditingTenant(null);
    };

    if (editingTenant) {
      updateMemberMutation.mutate(
        { id: editingTenant.id, ...payload },
        {
          onSuccess: () => { toast.success(`${tenantLabel} updated`); after(); },
          onError: (err) => toast.error(getApiErrorMessage(err)),
        }
      );
    } else {
      createMemberMutation.mutate(payload, {
        onSuccess: () => { toast.success(`${tenantLabel} added`); after(); },
        onError: (err) => toast.error(getApiErrorMessage(err)),
      });
    }
  };

  const toggleTenantActive = (tenant) => {
    const nextStatus = tenant.is_active ? 'inactive' : 'active';
    const verb = tenant.is_active ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${verb} ${tenant.name}? They will ${tenant.is_active ? 'stop' : 'resume'} counting toward this ${villaLabel.toLowerCase()}'s target.`)) return;
    updateMemberMutation.mutate(
      {
        id: tenant.id,
        name: tenant.name,
        phone_number: tenant.phone_number,
        email: tenant.email,
        member_no: tenant.member_no,
        house_id: selectedId,
        status: nextStatus,
      },
      {
        onSuccess: () => {
          toast.success(`${tenantLabel} ${nextStatus === 'active' ? 'reactivated' : 'deactivated'}`);
          queryClient.invalidateQueries({ queryKey: ['house', selectedId] });
          queryClient.invalidateQueries({ queryKey: ['houses'] });
        },
        onError: (err) => toast.error(getApiErrorMessage(err)),
      }
    );
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(`${villaLabel} Name/No is required`);
    createMutation.mutate(
      { name, description },
      {
        onSuccess: () => {
          setIsCreateOpen(false);
          setName('');
          setDescription('');
        },
      }
    );
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(`${villaLabel} Name/No is required`);
    updateMutation.mutate(
      { id: editingHouse.id, name, description },
      {
        onSuccess: () => {
          setIsEditOpen(false);
          setEditingHouse(null);
          setName('');
          setDescription('');
        },
      }
    );
  };

  const handleDelete = (id) => {
    if (window.confirm(`Are you sure you want to delete this ${villaLabel.toLowerCase()}? Members will be unassigned but NOT deleted.`)) {
      deleteMutation.mutate(id, {
        onSuccess: () => {
          if (selectedId === id) {
            setSelectedId(null);
          }
        }
      });
    }
  };

  const openEditModal = (sub) => {
    setEditingHouse(sub);
    setName(sub.name);
    setDescription(sub.description || '');
    setIsEditOpen(true);
  };

  const openCreateModal = () => {
    setName('');
    setDescription('');
    setIsCreateOpen(true);
  };

  // Main List Columns
  const listColumns = [
    {
      label: `${villaLabel} Name/No`,
      field: 'name',
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
            <Home size={20} />
          </div>
          <div>
            <span className="font-semibold text-gray-900 dark:text-white block">{row.name}</span>
            {row.description && <span className="text-xs text-gray-500 dark:text-gray-400 block max-w-xs truncate">{row.description}</span>}
          </div>
        </div>
      ),
    },
    {
      label: `${tenantLabel}s Count`,
      field: 'members_count',
      render: (row) => (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400">
          {row.members_count || 0} {tenantLabel}s
        </span>
      ),
    },

    {
      label: 'This Month',
      field: 'this_month_paid',
      render: (row) => {
        const paid = Number(row.this_month_paid || 0);
        const expected = Number(row.this_month_expected || 0);
        const actualPaid = Number(row.actual_month_paid ?? paid);
        const overpaid = Number(row.this_month_over || 0);
        const toArrears = Math.max(0, actualPaid - paid - overpaid);
        
        let infoText = '';
        if (overpaid > 0) infoText += `Includes KSh ${overpaid.toLocaleString()} advance overpayment.\n`;
        if (toArrears > 0) infoText += `KSh ${toArrears.toLocaleString()} deposited this month was applied to past arrears.\n`;
        if (paid === 0 && actualPaid > 0 && toArrears === 0) infoText += `KSh ${actualPaid.toLocaleString()} was deposited, but went to prior balances.\n`;

        if (!row.has_target) {
          return <span className="text-sm font-semibold text-gray-700">KSh {paid.toLocaleString()}</span>;
        }
        const settled = row.this_month_status === 'Settled';
        return (
          <div className="text-sm">
            <div className={`font-semibold flex items-center space-x-1.5 ${settled ? 'text-emerald-600' : 'text-gray-900 dark:text-white'}`}>
              <span>KSh {paid.toLocaleString()} / {expected.toLocaleString()}</span>
              {infoText && (
                <div title={infoText.trim()} className="text-blue-500 hover:text-blue-700 cursor-help flex-shrink-0 relative top-px">
                  <Info size={14} />
                </div>
              )}
            </div>
            <div className={`text-xs ${settled ? 'text-emerald-500' : 'text-amber-600 font-semibold'}`}>
              {row.this_month_status}
            </div>
          </div>
        );
      },
    },
    {
      label: 'Balance Status',
      field: 'balance_status',
      render: (row) => {
        if (!row.has_target) return <span className="text-gray-400">-</span>;
        const debt = Number(row.progress_debt || 0);
        const overpay = Number(row.progress_overpay || 0);
        if (debt > 0) {
          return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400">Arrears: KSh {debt.toLocaleString()}</span>;
        }
        if (overpay > 0) {
          return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400">Overpaid: KSh {overpay.toLocaleString()}</span>;
        }
        return <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">Up to date</span>;
      }
    },
    {
      label: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setSelectedId(row.id)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title="View Details"
          >
            <Eye size={18} />
          </button>
          <button
            onClick={() => openEditModal(row)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            title="Edit"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleExportDetail(row)}
            disabled={exportDetailMutation.isPending}
            className="p-1.5 rounded-lg text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50"
            title="Export Statement"
          >
            <Download size={18} />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
            title="Delete"
          >
            <Trash size={18} />
          </button>
        </div>
      ),
    },
  ];

  // All hooks above this point — render-time guards only from here on.
  if (shouldRedirect) {
    return <Navigate to="/my-house" replace />;
  }

  if (selectedId && detail) {
    const membersColumns = [
      {
        label: 'Name',
        field: 'name',
        render: (m) => (
          <div>
            <span className="font-medium text-gray-900 dark:text-white block">{m.name}</span>
            {m.member_no && <span className="text-xs text-gray-500 block">No. {m.member_no}</span>}
          </div>
        ),
      },
      {
        label: 'Phone Number',
        field: 'phone_number',
        render: (m) => <span>{m.phone_number || '-'}</span>,
      },
      {
        label: 'Email Address',
        field: 'email',
        render: (m) => <span className="text-gray-500">{m.email || '-'}</span>,
      },
      {
        label: 'Status',
        field: 'is_active',
        render: (m) => (
          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${m.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {m.is_active ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        label: 'Lifetime Contributions',
        field: 'total_contributions',
        render: (m) => (
          <span className="font-semibold text-emerald-600">
            KSh {Number(m.total_contributions || 0).toLocaleString()}
          </span>
        ),
      },
      {
        label: 'Actions',
        field: 'actions',
        render: (m) => (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => openEditTenant(m)}
              className="p-1.5 rounded-lg text-gray-500 hover:text-amber-600 hover:bg-amber-50 transition-colors"
              title={`Edit ${tenantLabel}`}
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => toggleTenantActive(m)}
              className={`p-1.5 rounded-lg transition-colors ${m.is_active ? 'text-gray-500 hover:text-rose-600 hover:bg-rose-50' : 'text-gray-500 hover:text-emerald-600 hover:bg-emerald-50'}`}
              title={m.is_active ? `Deactivate (${tenantLabel} relocated)` : `Reactivate ${tenantLabel}`}
            >
              {m.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
            </button>
          </div>
        ),
      },
    ];

    const statementColumns = [
      {
        label: 'Payment Date',
        field: 'contribution_date',
        render: (c) => (
          <div className="flex items-center space-x-2">
            <Calendar size={14} className="text-gray-400" />
            <span>{c.contribution_date || '-'}</span>
          </div>
        ),
      },
      {
        label: `${tenantLabel} Name`,
        field: 'member_name',
        render: (c) => (
          <div>
            <span className="font-semibold text-gray-800 dark:text-gray-200">{c.member_name}</span>
            {c.member_no && <span className="text-xs text-gray-500 block">No. {c.member_no}</span>}
          </div>
        ),
      },
      {
        label: 'Notes / Reference',
        field: 'notes',
        render: (c) => <span className="text-gray-500 italic text-sm">{c.notes || '-'}</span>,
      },
      {
        label: 'Payment Method',
        field: 'payment_method',
        render: (c) => (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 uppercase">
            {c.payment_method || '-'}
          </span>
        ),
      },
      {
        label: 'Amount Paid',
        field: 'amount',
        render: (c) => (
          <span className="font-bold text-emerald-600">
            + KSh {Number(c.amount || 0).toLocaleString()}
          </span>
        ),
      },
    ];

    const house = detail?.house || detail?.subgroup || {};
    const detailMembers = Array.isArray(detail?.members) ? detail.members : [];
    const detailContributions = Array.isArray(detail?.contributions) ? detail.contributions : [];

    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Detail Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSelectedId(null)}
              className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="flex items-center space-x-2">
                <Home size={28} className="text-blue-600" />
                <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">{house.name || 'House Details'}</h1>
              </div>
              <p className="text-sm text-gray-500 mt-1">{house.description || `Overview and consolidated statement of ${house.name || 'this house'}.`}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleExportDetail(house)}
              disabled={exportDetailMutation.isPending}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <Download size={16} />
              <span>{exportDetailMutation.isPending ? 'Exporting...' : 'Export Statement'}</span>
            </button>
            <button
              onClick={() => openEditModal(house)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors flex items-center space-x-2"
            >
              <Edit size={16} />
              <span>Edit Details</span>
            </button>
            <button
              onClick={() => handleDelete(house.id)}
              className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 font-medium transition-colors flex items-center space-x-2 shadow-sm"
            >
              <Trash size={16} />
              <span>Delete {villaLabel}</span>
            </button>
          </div>
        </div>

        {/* Stats summary */}
        <div className={`grid grid-cols-1 ${detail.has_target ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
              <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">Assigned {tenantLabel}s</span>
              <span className="text-3xl font-extrabold text-gray-900 dark:text-white mt-1 block">{detailMembers.length}</span>
            </div>
            <div className="p-4 rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
              <Users size={28} />
            </div>
          </div>

          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
              <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">All-Time Collected (Lifetime)</span>
              <span className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1 block">
                KSh {Number(detail.total_contributions || 0).toLocaleString()}
              </span>
              {Number(detail.drive_contributions_total || 0) > 0 && (
                <span className="text-xs text-gray-500 mt-1 block">
                  Paid to drives (separate): KSh {Number(detail.drive_contributions_total).toLocaleString()}
                </span>
              )}
            </div>
            <div className="p-4 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
              <CreditCard size={28} />
            </div>
          </div>

          {detail.has_target && (() => {
            const paid = Number(detail.this_month_paid || 0);
            const expected = Number(detail.this_month_expected || 0);
            const settled = detail.this_month_status === 'Settled';
            const captionCls = settled ? 'text-emerald-600' : 'text-amber-600';
            return (
              <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">This Month</span>
                  <span className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1 block">
                    KSh {Math.min(paid, expected).toLocaleString()} <span className="text-base font-medium text-gray-500">/ {expected.toLocaleString()}</span>
                  </span>
                  <span className={`text-sm font-semibold ${captionCls} block mt-1`}>
                    {detail.this_month_status}
                  </span>
                </div>
                <div className="p-4 rounded-full bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                  <Calendar size={28} />
                </div>
              </div>
            );
          })()}
        </div>

        {/* Progress Card with debt / paid / overpay segments */}
        {detail.has_target && (() => {
          const paidThru = Number(detail.progress_paid_to_date || 0);
          const expectedThru = Number(detail.progress_expected_to_date || 0);
          const debt = Number(detail.progress_debt || 0);
          const overpay = Number(detail.progress_overpay || 0);
          const totalPaid = Number(detail.target_paid || 0);
          const scale = Math.max(expectedThru, totalPaid, 1);
          const paidPct = (paidThru / scale) * 100;
          const debtPct = (debt / scale) * 100;
          const overpayPct = (overpay / scale) * 100;

          const statusLabel = debt > 0
            ? `Behind by KSh ${debt.toLocaleString()}`
            : overpay > 0
              ? `Overpaid by KSh ${overpay.toLocaleString()}`
              : expectedThru > 0
                ? 'Up to date'
                : 'No monthly target';
          const statusCls = debt > 0
            ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/20 dark:text-rose-400'
            : overpay > 0
              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
              : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400';
          return (
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Progress{detail.current_month_label ? ` · through ${detail.current_month_label}` : ''}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Target period: {detail.target_from_date ? new Date(detail.target_from_date).toLocaleDateString() : '-'} – {detail.target_to_date ? new Date(detail.target_to_date).toLocaleDateString() : '—'} &middot; KSh {Number(detail.this_month_expected || 0).toLocaleString()} expected each month
                </p>
              </div>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${statusCls}`}>
                {statusLabel}
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
                <span>Paid: <strong className="text-emerald-700 dark:text-emerald-400">KSh {paidThru.toLocaleString()}</strong></span>
                <span>Required to date: <strong className="text-gray-900 dark:text-white">KSh {expectedThru.toLocaleString()}</strong></span>
              </div>

              {/* Multi-segment bar: green paid, red debt, purple overpay */}
              <div className="w-full flex bg-gray-100 dark:bg-gray-700 h-3 rounded-full overflow-hidden">
                {paidPct > 0 && (
                  <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${paidPct}%` }} title={`Paid: KSh ${paidThru.toLocaleString()}`} />
                )}
                {debtPct > 0 && (
                  <div className="h-full bg-rose-500 transition-all duration-500" style={{ width: `${debtPct}%` }} title={`Debt: KSh ${debt.toLocaleString()}`} />
                )}
                {overpayPct > 0 && (
                  <div className="h-full bg-purple-500 transition-all duration-500" style={{ width: `${overpayPct}%` }} title={`Overpaid: KSh ${overpay.toLocaleString()}`} />
                )}
              </div>

              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Paid KSh {paidThru.toLocaleString()}</span>
                {debt > 0 && <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-500" /> Debt KSh {debt.toLocaleString()}</span>}
                {overpay > 0 && <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-500" /> Pre-pay KSh {overpay.toLocaleString()}</span>}
              </div>

              <p className="text-[11px] text-gray-400 dark:text-gray-500 pt-1">
                Paid in this calendar month: KSh {Number(detail.this_month_paid || 0).toLocaleString()} &middot; Total in period: KSh {totalPaid.toLocaleString()} of {Number(detail.target_amount || 0).toLocaleString()}
              </p>
            </div>

            {/* Monthly milestones */}
            {Array.isArray(detail.monthly_milestones) && detail.monthly_milestones.length > 0 && (() => {
              // Only show milestones that are actually relevant right now:
              //  - the current month (always)
              //  - any past month that's overdue (missed / short)
              //  - any month carrying overpayment overflow
              //  - the "progress" month (where the bucket landed)
              const milestones = detail.monthly_milestones;
              const isRelevant = (m) => {
                if (m.is_current) return true;
                if (detail.current_month_label && m.label === detail.current_month_label) return true;
                if (m.status === 'missed' || m.status === 'short' || m.status === 'due') return true;
                if (m.status === 'overpaid' || Number(m.overflow || 0) > 0) return true;
                return false;
              };
              let visible = showAllMilestones ? milestones : milestones.filter(isRelevant);
              // If filter is empty (e.g. brand-new villa with nothing yet), still surface the current/active month so the section isn't blank.
              if (visible.length === 0) {
                const curIdx = milestones.findIndex(m =>
                  m.is_current || (detail.current_month_label && m.label === detail.current_month_label)
                );
                visible = curIdx >= 0 ? [milestones[curIdx]] : [milestones[0]];
              }
              const visibleKeys = new Set(visible.map(m => m.key));
              const hiddenBefore = showAllMilestones ? 0 : milestones.findIndex(m => visibleKeys.has(m.key));
              const hiddenAfter = showAllMilestones
                ? 0
                : milestones.length - 1 - milestones.map(m => visibleKeys.has(m.key)).lastIndexOf(true);

              return (
              <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Monthly Milestones</h4>
                    <p className="text-xs text-gray-500">
                      KSh {Number(detail.expected_per_month || 0).toLocaleString()} expected each month &middot; {detail.months_completed}/{detail.total_months} months covered
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {detail.current_month_label && (
                      <span className="text-xs text-gray-500">Now: <strong>{detail.current_month_label}</strong></span>
                    )}
                    {(showAllMilestones || hiddenBefore > 0 || hiddenAfter > 0) && (
                      <button
                        onClick={() => setShowAllMilestones(v => !v)}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline"
                      >
                        {showAllMilestones ? 'Show relevant only' : `Show all ${milestones.length}`}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {hiddenBefore > 0 && (
                    <button
                      onClick={() => setShowAllMilestones(true)}
                      className="px-2 py-1 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200"
                      title={`${hiddenBefore} earlier month(s) hidden`}
                    >
                      ← +{hiddenBefore}
                    </button>
                  )}
                  {visible.map((m) => {
                    const cls = m.status === 'paid'
                      ? 'bg-emerald-500 border-emerald-600 text-white'
                      : m.status === 'overpaid'
                      ? 'bg-purple-500 border-purple-600 text-white'
                      : m.status === 'partial'
                      ? 'bg-blue-500 border-blue-600 text-white'
                      : m.status === 'short'
                      ? 'bg-amber-400 border-amber-500 text-amber-950'
                      : (m.status === 'missed' || m.status === 'due')
                      ? 'bg-rose-500 border-rose-600 text-white'
                      : 'bg-gray-100 border-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
                    const ring = m.is_current ? 'ring-2 ring-blue-500 ring-offset-1' : '';
                    const title = `${m.label}: KSh ${Number(m.paid).toLocaleString()} / ${Number(m.expected).toLocaleString()}${m.overflow ? ` (+${Number(m.overflow).toLocaleString()} overflow)` : ''} — ${m.status}`;
                    return (
                      <div
                        key={m.key}
                        title={title}
                        className={`px-2 py-1 rounded-md text-[10px] font-semibold border ${cls} ${ring} min-w-[64px] text-center`}
                      >
                        <div>{m.label}</div>
                        {m.overflow > 0 && <div className="text-[9px] opacity-90">+{Number(m.overflow).toLocaleString()}</div>}
                      </div>
                    );
                  })}
                  {hiddenAfter > 0 && (
                    <button
                      onClick={() => setShowAllMilestones(true)}
                      className="px-2 py-1 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200"
                      title={`${hiddenAfter} later month(s) hidden`}
                    >
                      +{hiddenAfter} →
                    </button>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Paid</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-500" /> Partial (pre-pay)</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-400" /> Short (past)</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-500" /> Missed / Due</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-500" /> Overpaid</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-200 border border-gray-300" /> Pending</span>
                </div>
              </div>
              );
            })()}
          </div>
          );
        })()}

        {/* Members and Statements tabs */}
        <div className="space-y-6">
          {/* Co-Members */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <Users size={20} className="text-gray-500" />
                <span>Occupants / Co-{tenantLabel}s</span>
              </h2>
              <button
                onClick={openAddTenant}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center space-x-1.5"
              >
                <UserPlus size={16} />
                <span>Add {tenantLabel}</span>
              </button>
            </div>
            <Table
              columns={membersColumns}
              data={detailMembers}
              loading={isDetailLoading}
            />
          </div>

          {/* Unified Statement ledger */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <FileText size={20} className="text-gray-500" />
              <span>Consolidated {villaLabel} Statement</span>
            </h2>
            <Table
              columns={statementColumns}
              data={detailContributions}
              loading={isDetailLoading}
            />
          </div>
        </div>

        {/* Tenant create/edit modal */}
        <Modal
          isOpen={isTenantModalOpen}
          onClose={() => { setIsTenantModalOpen(false); setEditingTenant(null); }}
          title={`${editingTenant ? 'Edit' : 'Add'} ${tenantLabel}`}
        >
          <form onSubmit={handleTenantSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={tenantForm.name}
                  onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="text"
                  value={tenantForm.phone_number}
                  onChange={(e) => setTenantForm({ ...tenantForm, phone_number: e.target.value })}
                  placeholder="+2547..."
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={tenantForm.email}
                  onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tenantLabel} No.</label>
                <input
                  type="text"
                  value={tenantForm.member_no}
                  onChange={(e) => setTenantForm({ ...tenantForm, member_no: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {editingTenant && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={tenantForm.status}
                    onChange={(e) => setTenantForm({ ...tenantForm, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive (relocated)</option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => { setIsTenantModalOpen(false); setEditingTenant(null); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
                disabled={createMemberMutation.isPending || updateMemberMutation.isPending}
              >
                {(createMemberMutation.isPending || updateMemberMutation.isPending) ? 'Saving...' : (editingTenant ? 'Save Changes' : `Add ${tenantLabel}`)}
              </button>
            </div>
          </form>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setEditingHouse(null);
          }}
          title={`Edit ${villaLabel}`}
        >
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{villaLabel} Name/No *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Apartment A1"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Ground floor apartment, 2 bedrooms"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsEditOpen(false);
                  setEditingHouse(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    );
  }

  // Otherwise, render list view
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white flex items-center space-x-3">
            <Home className="text-blue-600" size={32} />
            <span>{villaLabel}s Management</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">
            Create, view, and manage physical grouping structures ({villaLabel.toLowerCase()}s) and consolidate statement summation balances.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsImportOpen(true)}
            className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors flex items-center space-x-2"
          >
            <Upload size={16} />
            <span>Import</span>
          </button>
          <button
            onClick={handleExportAll}
            disabled={exportAllMutation.isPending || houses.length === 0}
            className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
            title={`Export all ${villaLabel.toLowerCase()}s as CSV`}
          >
            <Download size={16} />
            <span>{exportAllMutation.isPending ? 'Exporting...' : 'Export All'}</span>
          </button>
          <button
            onClick={openCreateModal}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg active:scale-95"
          >
            <Plus size={20} />
            <span>Add {villaLabel}</span>
          </button>
        </div>
      </div>

      {/* Aggregated summary */}
      {houses.length > 0 && (
        (() => {
          const totals = houses.reduce((acc, s) => {
            const bk = s.current_month_breakdown || {};
            const actualPaid = Number(s.actual_month_paid ?? bk.actual_month_paid ?? s.this_month_paid ?? 0);
            const thisMonthPaid = Number(s.this_month_paid || 0);
            const thisMonthExpected = Number(s.this_month_expected || 0);

            const toArrears = bk.arrears_applied !== undefined
              ? Number(bk.arrears_applied || 0)
              : Math.max(0, actualPaid - thisMonthPaid);

            const toCurrentMonth = bk.current_month_applied !== undefined
              ? Number(bk.current_month_applied || 0)
              : Math.min(Math.max(0, actualPaid - toArrears), thisMonthExpected);

            const newOverpay = bk.future_applied !== undefined
              ? Number(bk.future_applied || 0)
              : Math.max(0, actualPaid - toCurrentMonth - toArrears);
            
            const totalDebt = Number(s.progress_debt || 0);
            const thisMonthDebt = Math.max(0, thisMonthExpected - thisMonthPaid);
            const prevMonthDebt = Math.max(0, totalDebt - thisMonthDebt);
            
            const cappedThisMonthPaid = Math.min(thisMonthPaid, thisMonthExpected);
            
            return {
              allPaid: acc.allPaid + Number(s.total_contributions || 0),
              monthPaid: acc.monthPaid + cappedThisMonthPaid,
              monthExpected: acc.monthExpected + thisMonthExpected,
              actualPaid: acc.actualPaid + actualPaid,
              totalDebt: acc.totalDebt + totalDebt,
              thisMonthDebt: acc.thisMonthDebt + thisMonthDebt,
              prevMonthDebt: acc.prevMonthDebt + prevMonthDebt,
              totalOverpay: acc.totalOverpay + Number(s.progress_overpay || 0),
              totalToArrears: acc.totalToArrears + toArrears,
              totalToCurrentMonth: acc.totalToCurrentMonth + toCurrentMonth,
              totalNewOverpay: acc.totalNewOverpay + newOverpay,
            };
          }, { allPaid: 0, monthPaid: 0, monthExpected: 0, actualPaid: 0, totalDebt: 0, thisMonthDebt: 0, prevMonthDebt: 0, totalOverpay: 0, totalToArrears: 0, totalToCurrentMonth: 0, totalNewOverpay: 0 });
          
          return (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 kpi-grid">
              <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
                <div>
                  <div className="flex items-center space-x-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">This Month's Target (All {villaLabel}s)</span>
                    <Info size={12} className="text-gray-400 cursor-help" title={`The total amount expected from all active ${villaLabel.toLowerCase()}s for the current billing cycle.`} />
                  </div>
                  <span className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1 block">KSh {totals.monthPaid.toLocaleString()} / {totals.monthExpected.toLocaleString()}</span>
                  <div className="mt-2">
                    <span className="text-xs font-medium text-amber-600 block">
                      Remaining to collect: KSh {Math.max(0, totals.monthExpected - totals.monthPaid).toLocaleString()}
                    </span>
                    <span className="text-[9px] text-gray-400 block mt-0.5">
                      (Excludes advance overpayments)
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
                <div>
                  <div className="flex items-center space-x-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">This Month's Collections</span>
                    <Info size={12} className="text-gray-400 cursor-help" title="Total money collected this month, including payments toward arrears or advances." />
                  </div>
                  <span className="text-2xl font-extrabold text-blue-600 dark:text-blue-400 mt-1 block">
                    KSh {totals.actualPaid.toLocaleString()}
                  </span>
                  <div className="mt-2 space-y-0.5">
                    {totals.monthExpected > 0 && (
                      <span className="text-xs font-medium text-gray-500 block">
                        Target: KSh {totals.monthExpected.toLocaleString()}
                      </span>
                    )}
                    {totals.totalToArrears > 0 && (
                      <span className="text-xs font-medium text-amber-600 block">
                        • KSh {totals.totalToArrears.toLocaleString()} filled past arrears
                      </span>
                    )}
                    {totals.totalToCurrentMonth > 0 && (
                      <span className="text-xs font-medium text-emerald-600 block">
                        • KSh {totals.totalToCurrentMonth.toLocaleString()} went to this month's contribution
                      </span>
                    )}
                    {totals.totalNewOverpay > 0 && (
                      <span className="text-xs font-medium text-purple-600 block">
                        • KSh {totals.totalNewOverpay.toLocaleString()} went to overpayment (advance)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center space-x-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">Total Arrears (Past Due)</span>
                    <Info size={12} className="text-gray-400 cursor-help" title="Unpaid balances from previous months combined with any unpaid amount for the current month." />
                  </div>
                  <span className="text-[9px] text-gray-400 block mb-1">Includes current and previous months</span>
                  <span className={`text-2xl font-extrabold mt-1 block ${totals.totalDebt > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                    {totals.totalDebt > 0 ? `KSh ${totals.totalDebt.toLocaleString()}` : 'No Arrears'}
                  </span>
                  {totals.totalDebt > 0 && (
                    <div className="mt-2 space-y-0.5">
                      <span className="text-xs font-medium text-rose-500 block">
                        • This month: KSh {totals.thisMonthDebt.toLocaleString()}
                      </span>
                      <span className="text-xs font-medium text-rose-700 block">
                        • Previous months: KSh {totals.prevMonthDebt.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
                {totals.totalDebt > 0 && (
                  <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                    <UserX size={48} className="text-rose-600" />
                  </div>
                )}
              </div>

              <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center space-x-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">Total Overpayments (Advances)</span>
                    <Info size={12} className="text-gray-400 cursor-help" title={`Total advance payments made by ${villaLabel.toLowerCase()}s that exceed their expected target.`} />
                  </div>
                  <span className={`text-2xl font-extrabold mt-1 block ${totals.totalOverpay > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                    {totals.totalOverpay > 0 ? `KSh ${totals.totalOverpay.toLocaleString()}` : 'None'}
                  </span>
                </div>
                {totals.totalOverpay > 0 && (
                  <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                    <CreditCard size={48} className="text-purple-600" />
                  </div>
                )}
              </div>
            </div>
          );
        })()
      )}

      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={`Search ${villaLabel.toLowerCase()}s by name, description, or occupant...`}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <Table
          columns={listColumns}
          data={paginatedHouses}
          loading={isListLoading}
          page={page}
          perPage={perPage}
          total={houses.length}
          onPageChange={setPage}
        />
      </div>

      {/* Import Modal */}
      <Modal
        isOpen={isImportOpen}
        onClose={() => { setIsImportOpen(false); setImportFile(null); }}
        title={`Bulk Import ${villaLabel}s`}
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Upload a CSV with one row per {villaLabel.toLowerCase()}. Required columns:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside mb-3">
              <li>Name (required) — the {villaLabel.toLowerCase()} name or number</li>
              <li>Description (optional)</li>
            </ul>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              disabled={templateMutation.isPending}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
            >
              <Download size={16} />
              {templateMutation.isPending ? 'Preparing...' : 'Download Template'}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {importFile && (
              <p className="text-xs text-gray-500 mt-1">Selected: {importFile.name}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => { setIsImportOpen(false); setImportFile(null); }}
              disabled={importMutation.isPending}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImportSubmit}
              disabled={importMutation.isPending || !importFile}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm disabled:opacity-50"
            >
              {importMutation.isPending ? 'Uploading...' : `Import ${villaLabel}s`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title={`Add New ${villaLabel}`}
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{villaLabel} Name/No *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Apartment A1"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Ground floor apartment, 2 bedrooms"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => setIsCreateOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : `Create ${villaLabel}`}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal (when detail view is not active but editing from list) */}
      <Modal
        isOpen={isEditOpen && !selectedId}
        onClose={() => {
          setIsEditOpen(false);
          setEditingHouse(null);
        }}
        title={`Edit ${villaLabel}`}
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{villaLabel} Name/No *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Apartment A1"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Ground floor apartment, 2 bedrooms"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setIsEditOpen(false);
                setEditingHouse(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
